import React from "react"
import { Badge, Button, Card, CardBody } from "reactstrap"
import { actorName, lineName, logTime, plural, quantityLabel, sheetStatuses } from "./labels"
import { COURIER_UNCONFIGURED, abroad, activeAwbOf, asksResend, blockerText, dayLabel, emailHeeded, emailLine, fixes, mainShipment, nothingToSend,
  shipmentWeightLabel, stoppedTitle, transportName, warningText, weightLabel } from "./shipmentLabels"
import { sheetInProcessing, sheetReadyToShip, sheetShipped, sheetWithoutShipment, shipmentAwaitsGoods, shipmentShipped } from "./sheetState"
import GenerationState from "./GenerationState"

const icons = { ok: "bx-check-circle text-success", block: "bx-x-circle text-danger", heed: "bx-error text-warning", info: "bx-info-circle text-info" }

// One item of the panel, with the link to the section that resolves it.
function Item({ state, fix, children }) {
  return <li className="d-flex mb-1"><i className={`bx ${icons[state]} mr-2 mt-1`} aria-hidden="true" />
    <div><span>{children}</span>{fix && <>{" · "}<a href={fix[0]}>{fix[1]}</a></>}</div>
  </li>
}

// „Ready to validate?” (US 70, D13): what blocks the sheet's validation, what to heed and what to check, each with a link to what resolves
// it, and Validate sheet. Something that blocks only a courier leaves a validation without one possible. An address in Romania also offers
// the AI prompt for the AWB (US 97, 111), once nothing blocks a courier: the AWB it brings back is added at Validate sheet, or later.
function Preparing({ sheet, pending, onValidateSheet, onPrompt }) {
  const { blockers, warnings, checks } = sheet.readiness
  if (nothingToSend(sheet.readiness)) return <PreparingWithoutShipment sheet={sheet} pending={pending} onValidateSheet={onValidateSheet} />
  const has = kind => blockers.courier.find(blocker => blocker.kind === kind)
  const courierOnly = blocker => !blockers.noCourier.some(other => other.kind === blocker.kind)
  const blocking = blocker => <Item key={blocker.kind} state="block" fix={fixes[blocker.kind]}>
    {blockerText(blocker, sheet)}{courierOnly(blocker) ? " (needed for a courier)" : ""}
  </Item>
  const main = mainShipment(sheet)
  const romanian = !abroad(sheet.delivery?.address)
  const link = (href, text) => <a href={href}>{text}</a>
  return <>
    <h4 className="card-title">{blockers.courier.length ? "Ready to validate?"
      : <><i className="bx bx-check-circle text-success mr-1" aria-hidden="true" />Ready to validate</>}</h4>
    <p className="small text-muted"><Badge color="soft-warning">In progress</Badge>{main?.setWeight != null ? ` · ${shipmentWeightLabel(main)}`
      : weightLabel(main?.weight) ? ` · products ${weightLabel(main.weight)}` : ""}</p>
    <ul className="list-unstyled small mb-2">
      {has("toComplete") ? blocking(has("toComplete")) : <Item state="ok">All quantities complete</Item>}
      {has("address") || has("addressToConfirm") ? [has("address"), has("addressToConfirm")].filter(Boolean).map(blocking)
        : <Item state="ok">Delivery address confirmed</Item>}
      {has("contact") ? blocking(has("contact")) : <Item state="ok">Contact complete</Item>}
      {has("empty") && blocking(has("empty"))}
      <Item state="info">The AWB can be added now or at processing</Item>
      {warnings.courier.map(warning => <Item key={warning.kind} state="heed" fix={fixes[warning.kind]}>{warningText(warning)}</Item>)}
      {checks.map(check => ({
        deliverySignals: () => <Item key={check.kind} state="heed">{link("#logistics-delivery", "The event changed since the delivery was chosen")}</Item>,
        toReview: () => <Item key={check.kind} state="heed">{link("#logistics-products", `${plural(check.lines.length, "line")} to review`)}</Item>,
        estimated: () => <Item key={check.kind} state="info">
          {link("#logistics-competitions", `${plural(check.competitions, "competition")} with an estimated additional phase`)}
        </Item>,
      }[check.kind] || (() => null))())}
    </ul>
    {blockers.courier.length > 0 && <p className="small text-danger">{blockers.noCourier.length
      ? `${plural(blockers.courier.length, "blocking item")} before validation.`
      : `${plural(blockers.courier.length, "blocking item")} for a courier. Without a courier, only the contact's name and phone are needed.`}</p>}
    <p className="small text-muted">Validate sheet locks the quantities, shows the requirements in SportyaOS and emails them to the organizer.</p>
    <Button color="primary" block disabled={pending || blockers.noCourier.length > 0} onClick={onValidateSheet}>
      <i className="bx bx-check-shield mr-1" aria-hidden="true" />Validate sheet
    </Button>
    {romanian && <Button color="primary" outline block disabled={pending || blockers.courier.length > 0} onClick={onPrompt}>
      <i className="bx bx-bot mr-1" aria-hidden="true" />Generate AI prompt
    </Button>}
  </>
}

// „Ready to validate” with nothing to send from MPG (D12): only lines to complete block; neither an address, a contact nor an AWB is needed.
function PreparingWithoutShipment({ sheet, pending, onValidateSheet }) {
  const { blockers, warnings, checks } = sheet.readiness
  const toComplete = blockers.noShipment.find(blocker => blocker.kind === "toComplete")
  return <>
    <h4 className="card-title">{toComplete ? "Ready to validate?"
      : <><i className="bx bx-check-circle text-success mr-1" aria-hidden="true" />Ready to validate</>}</h4>
    <p className="small text-muted"><Badge color="soft-warning">In progress</Badge></p>
    <ul className="list-unstyled small mb-2">
      {toComplete ? <Item state="block" fix={fixes.toComplete}>{blockerText(toComplete, sheet)}</Item> : <Item state="ok">All quantities complete</Item>}
      {blockers.noShipment.filter(blocker => blocker.kind !== "toComplete").map(blocker => <Item key={blocker.kind} state="block" fix={fixes[blocker.kind]}>
        {blockerText(blocker, sheet)}</Item>)}
      <Item state="info">Nothing to ship from MPG: the club provides every product</Item>
      {warnings.noShipment.map(warning => <Item key={warning.kind} state="heed" fix={fixes[warning.kind]}>{warningText(warning)}</Item>)}
      {checks.map(check => check.kind === "estimated" ? <Item key={check.kind} state="info"><a href="#logistics-competitions">
        {`${plural(check.competitions, "competition")} with an estimated additional phase`}</a></Item> : null)}
    </ul>
    <p className="small text-muted">Validate sheet locks the quantities, shows the requirements in SportyaOS and emails them to the organizer.</p>
    <Button color="primary" block disabled={pending || blockers.noShipment.length > 0} onClick={onValidateSheet}>
      <i className="bx bx-check-shield mr-1" aria-hidden="true" />Validate sheet
    </Button>
  </>
}

// "Event requirements email: sent to 2 recipients", or each state with how many deliveries have it: the email of the sheet's validation.
// The main shipment's emails, each summed up (emailLine): the requirements, then the AWB email once one went.
function Emails({ main }) {
  return (main?.emails?.length ? main.emails : main?.email ? [main.email] : []).filter(email => email.deliveries.length)
    .map(email => <Item key={email.origin || email.message} state={emailHeeded(email) ? "heed" : "ok"}>{emailLine(email)}</Item>)
}

// „Ready for processing” (D13–D14): the sheet validated, by whom and when, with the transport chosen and the AWB once given, its emails
// and the requirements shown in SportyaOS. The warehouse processes the main shipment (Validate processing), its AWB entered by hand,
// added here (Add AWB) or changed (Change AWB), or generated through Cargus for a Romanian address, which is the processing too and makes
// Cargus the courier; the AI prompt helps create the AWB. Reopen sheet brings it back in preparation.
function Processing({ sheet, timezone, hasCourier, pending, onProcess, onChangeAwb, onGenerate, onPrompt, onReopen }) {
  const main = mainShipment(sheet)
  const blockers = sheet.readiness?.blockers || { courier: [], noCourier: [] }
  const romanian = !abroad(main?.address)
  const courier = main?.transport === "courier"
  const cargus = courier && romanian
  return <>
    <h4 className="card-title">Ready for processing</h4>
    <p className="small text-muted">{[`Validated by ${actorName(main?.sheetValidatedBy)}`, logTime(main?.sheetValidatedAt, timezone),
      transportName(main || {})].filter(Boolean).join(" · ")}</p>
    <ul className="list-unstyled small mb-2">
      {courier && (main.awb ? <Item state="ok">{`${transportName(main)} · AWB ${main.awb}`}</Item>
        : <Item state="info">No AWB yet: add it here or at processing</Item>)}
      <Emails main={main} />
      <Item state="ok">Shown to the organizer in SportyaOS</Item>
    </ul>
    {main?.generation && <GenerationState generation={main.generation} pending={pending || !hasCourier} onRetry={onGenerate} />}
    <Button color="primary" block disabled={pending} onClick={onProcess}><i className="bx bx-package mr-1" aria-hidden="true" />Validate processing</Button>
    {courier && main.awbSource !== "generated" && <Button color="primary" outline block disabled={pending} onClick={onChangeAwb}>
      <i className="bx bx-barcode mr-1" aria-hidden="true" />{main.awb ? "Change AWB" : "Add AWB"}
    </Button>}
    {cargus && <Button color="primary" outline block disabled={pending || !hasCourier || blockers.courier.length > 0} onClick={onGenerate}>
      <i className="bx bx-barcode mr-1" aria-hidden="true" />Generate AWB
    </Button>}
    {courier && romanian && <Button color="primary" outline block disabled={pending || blockers.courier.length > 0} onClick={onPrompt}>
      <i className="bx bx-bot mr-1" aria-hidden="true" />Generate AI prompt
    </Button>}
    {cargus && !hasCourier && <p className="small text-muted mt-1">
      {`${COURIER_UNCONFIGURED}: create the AWB in WebExpress (AI prompt) and enter it at processing.`}
    </p>}
    <Button color="light" block disabled={pending} onClick={onReopen}><i className="bx bx-undo mr-1" aria-hidden="true" />Reopen sheet</Button>
  </>
}

// „Ready to be shipped” (D14–D17): processed by whom and when, with its parcels and weight, its AWB and, for an AWB Cargus generated, when
// the courier picks it up; its emails. Mark as shipped ships it, the stock moving then (D15). Its AWB still changes (Change AWB), unless
// Cargus generated it; Undo processing brings it back to ready for processing.
function Ready({ sheet, timezone, pending, onShip, onChangeAwb, onUndo }) {
  const main = mainShipment(sheet)
  const courier = main?.transport === "courier"
  return <>
    <h4 className="card-title">Ready to be shipped</h4>
    <p className="small text-muted">{[`Processed by ${actorName(main?.processedBy)}`, logTime(main?.processedAt, timezone),
      ...(courier ? [plural(main.parcels, "parcel"), weightLabel(main.weight)] : [transportName(main || {})])].filter(Boolean).join(" · ")}</p>
    <ul className="list-unstyled small mb-2">
      {courier && <Item state="ok">{`${transportName(main)} · AWB ${main.awb}`}</Item>}
      {courier && main.pickupDay && <Item state="info">{`Estimated pickup: ${dayLabel(main.pickupDay)}`}</Item>}
      <Emails main={main} />
      <Item state="info">The stock decreases at Mark as shipped</Item>
    </ul>
    <Button color="primary" block disabled={pending} onClick={onShip}><i className="bx bx-send mr-1" aria-hidden="true" />Mark as shipped</Button>
    {courier && main.awbSource !== "generated" && <Button color="primary" outline block disabled={pending} onClick={onChangeAwb}>
      <i className="bx bx-barcode mr-1" aria-hidden="true" />Change AWB
    </Button>}
    <Button color="light" block disabled={pending} onClick={onUndo}><i className="bx bx-undo mr-1" aria-hidden="true" />Undo processing</Button>
  </>
}

// „No shipment” (D12): validated by whom and when, nothing to ship from MPG, its requirements email, resent once a delivery failed or is
// unconfirmed (Tudor, 2026-10-21), and the requirements shown in SportyaOS. Reopen sheet brings it back in preparation.
function NoShipment({ sheet, timezone, pending, onResend, onReopen }) {
  const { validation } = sheet
  const resent = validation?.email?.recipients?.some(({ status }) => asksResend(status))
  return <>
    <h4 className="card-title">No shipment</h4>
    <p className="small text-muted">{[`Validated by ${actorName(validation?.sheetValidatedBy)}`, logTime(validation?.sheetValidatedAt, timezone)]
      .filter(Boolean).join(" · ")}</p>
    <p className="small">Nothing to ship from MPG: the club provides every product. No courier, no AWB, no stock movement.</p>
    <ul className="list-unstyled small mb-2">
      <Emails main={validation} />
      <Item state="ok">Shown to the organizer in SportyaOS</Item>
    </ul>
    {resent && <Button color="primary" outline block disabled={pending} onClick={onResend}>
      <i className="bx bx-envelope mr-1" aria-hidden="true" />Resend the requirements email
    </Button>}
    <Button color="light" block disabled={pending} onClick={onReopen}><i className="bx bx-undo mr-1" aria-hidden="true" />Reopen sheet</Button>
  </>
}

// A shipped sheet: when and how its main shipment left, where the calculation now differs from what MPG sent, supplementary shipments
// included (US 80), a supplementary shipment that has not left yet, a new one and a return.
function Shipped({ sheet, pending, onSupplementary, onReturn }) {
  const main = mainShipment(sheet)
  const differing = sheet.lines.filter(line => line.supplier === "mpg" && line.marks.includes("calculationDiffers") && !line.variants)
  const preparing = (sheet.shipments || []).some(shipment => shipment.kind === "supplementary" && shipmentAwaitsGoods(shipment.status))
  return <>
    <h4 className="card-title"><i className="bx bx-check-circle text-success mr-1" aria-hidden="true" />Shipped</h4>
    {main && <p className="small text-muted">{`${dayLabel(main.date)} · ${main.transport === "noCourier" ? "Without courier" : `AWB ${main.awb}`}`}</p>}
    <p className="small">Effective quantities are locked at validation.</p>
    {differing.map(line => <p key={line.key} className="small">{`${lineName(line, sheet.officialBall)}: calculated now ${quantityLabel(line.calculated, line.unit)}, `
      + `sent ${quantityLabel(line.effective + (line.supplementary || 0), line.unit)}.`}</p>)}
    {preparing && <p className="small text-info">
      <span>A supplementary shipment has not left yet.</span> <a href="#logistics-shipments">Go to shipments</a>
    </p>}
    <Button color="primary" outline block disabled={pending} onClick={onSupplementary}><i className="bx bx-plus mr-1" aria-hidden="true" />Supplementary shipment</Button>
    {shipmentShipped(main?.status) && <Button color="light" block disabled={pending} onClick={onReturn}><i className="bx bx-undo mr-1" aria-hidden="true" />Record return</Button>}
    <p className="small text-muted mt-2 mb-0">Corrections: use Correct (<i className="bx bx-edit-alt" aria-hidden="true" />) on a product line, with a reason.</p>
  </>
}

// A canceled or deleted event's sheet (D10): read-only, in the state it stopped in; the AWB its main shipment still holds, if it did not leave,
// is cancelled with a reason (Tudor, 2026-10-22). Its returns and the signal's closing are on the band under the sheet's title.
function Canceled({ sheet, eventStatus, pending, onCancelAwb }) {
  const awb = activeAwbOf(mainShipment(sheet))?.awb
  const state = sheetStatuses[sheet.status] || { label: sheet.status, color: "secondary" }
  return <>
    <h4 className="card-title">{stoppedTitle(eventStatus)}</h4>
    <p className="small text-muted"><Badge color={`soft-${state.color}`}>{state.label}</Badge></p>
    {awb ? <>
      <p className="small text-warning">{`AWB ${awb} of the main shipment is still active.`}</p>
      <Button color="danger" outline block disabled={pending} onClick={onCancelAwb}><i className="bx bx-x-circle mr-1" aria-hidden="true" />Cancel the AWB</Button>
    </> : <p className="small text-muted mb-0">Nothing moves toward shipping any more.</p>}
  </>
}

// The readiness panel of the Logistics tab, beside the sheet from 1200 px and above it below, by the sheet's state, or by its event's once
// it is canceled or deleted (`eventStatus`).
export default function ShipmentPanel({ sheet, timezone, eventStatus, hasCourier = true, pending, onValidateSheet, onProcess, onChangeAwb, onUndo, onShip,
  onGenerate, onPrompt, onReopen, onResend, onSupplementary, onReturn, onCancelAwb, exporting, onExport }) {
  const shipped = sheetShipped(sheet.status)
  const processing = sheetInProcessing(sheet.status)
  const ready = sheetReadyToShip(sheet.status)
  const withoutShipment = sheetWithoutShipment(sheet.status)
  if (!sheet.eventCanceled && !shipped && !processing && !ready && !withoutShipment && !sheet.readiness) return null
  const label = sheet.eventCanceled ? stoppedTitle(eventStatus) : shipped ? "Shipped" : ready ? "Ready to be shipped" : processing ? "Ready for processing"
    : withoutShipment ? "No shipment" : "Ready to validate?"
  return <Card tag="section" aria-label={label} className="logistics-panel"><CardBody>
    {sheet.eventCanceled ? <Canceled sheet={sheet} eventStatus={eventStatus} pending={pending} onCancelAwb={onCancelAwb} />
      : shipped ? <Shipped sheet={sheet} pending={pending} onSupplementary={onSupplementary} onReturn={onReturn} />
      : withoutShipment ? <NoShipment sheet={sheet} timezone={timezone} pending={pending} onResend={onResend} onReopen={onReopen} />
      : ready ? <Ready sheet={sheet} timezone={timezone} pending={pending} onShip={onShip} onChangeAwb={onChangeAwb} onUndo={onUndo} />
        : processing ? <Processing sheet={sheet} timezone={timezone} hasCourier={hasCourier} pending={pending} onProcess={onProcess} onChangeAwb={onChangeAwb}
          onGenerate={onGenerate} onPrompt={onPrompt} onReopen={onReopen} />
          : <Preparing sheet={sheet} pending={pending} onValidateSheet={onValidateSheet} onPrompt={onPrompt} />}
    <Button color="light" block className="mt-2" disabled={exporting} onClick={onExport}>
      <i className="bx bx-download mr-1" aria-hidden="true" />{exporting ? "Preparing XLSX…" : "Export XLSX"}
    </Button>
  </CardBody></Card>
}
